import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Button, PasswordInput, StatusBanner, ScreenHeader } from './ui';
import {
  IconFile,
  IconUpload,
  IconAlert,
  IconBolt,
} from './icons';

interface UploadZoneProps {
  onFileSelect: (file: File, password: string) => void;
  isProcessing: boolean;
  progress: number;
  initialPassword?: string;
  /**
   * Pre-seed the zone with a file that was already picked/dropped on a
   * previous screen (e.g. the unified home-layout drop zone). The user
   * lands straight on the password prompt — no re-pick needed.
   */
  initialFile?: File | null;
  /**
   * Notifies the parent whenever the user picks/drops/clears a file or
   * edits the password. The parent stores these in state so they survive
   * unmounts (e.g. when the ErrorView overlay is shown after a wrong
   * password) and can be re-seeded as `initialFile` / `initialPassword`.
   */
  onFileChange?: (file: File | null) => void;
  onPasswordChange?: (password: string) => void;
  onEvent?: (event: string, metadata?: any) => void;
}

export const UploadZone: React.FC<UploadZoneProps> = ({
  onFileSelect,
  isProcessing,
  progress,
  initialPassword = '',
  initialFile = null,
  onFileChange,
  onPasswordChange,
  onEvent,
}) => {
  const [file, setFile] = useState<File | null>(initialFile);
  const [password, setPassword] = useState(initialPassword);
  const [isDragOver, setIsDragOver] = useState(false);
  const [dropError, setDropError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef(password);
  passwordRef.current = password;

  // Re-seed local state when parent changes the initial values (e.g. on
  // remount after the ErrorView overlay is dismissed).
  // Guard: only update if the incoming value genuinely differs from what
  // the user has typed — prevents the feedback loop where onPasswordChange
  // pushes the typed value up to the parent, which flows back as
  // initialPassword and triggers an extra re-render on every keystroke.
  useEffect(() => {
    if (initialPassword && initialPassword !== passwordRef.current) {
      setPassword(initialPassword);
    }
  }, [initialPassword]);
  useEffect(() => {
    if (initialFile) setFile(initialFile);
  }, [initialFile]);

  const updateFile = useCallback(
    (next: File | null) => {
      setFile(next);
      onFileChange?.(next);
    },
    [onFileChange],
  );

  const updatePassword = useCallback(
    (next: string) => {
      setPassword(next);
      onPasswordChange?.(next);
    },
    [onPasswordChange],
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragOver(false);
      setDropError(null);
      const dropped = e.dataTransfer.files[0];
      if (dropped?.type === 'application/pdf') {
        updateFile(dropped);
        onEvent?.('FILE_SELECTED', { filename: dropped.name, size: dropped.size });
      } else if (dropped) {
        setDropError('That doesn\'t look like a PDF. Please drop a PDF file.');
        setTimeout(() => setDropError(null), 3500);
      }
    },
    [onEvent, updateFile],
  );

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0];
    if (selected) {
      updateFile(selected);
      setDropError(null);
      onEvent?.('FILE_SELECTED', { filename: selected.name, size: selected.size });
    }
  };

  const handleSubmit = () => {
    if (file) onFileSelect(file, password);
  };

  if (isProcessing) {
    return (
      <div style={{ textAlign: 'center', padding: '40px 0' }} aria-live="polite">
        <span
          style={{
            display: 'inline-flex',
            width: 56,
            height: 56,
            borderRadius: '50%',
            backgroundColor: 'var(--pc-color-primary-soft)',
            color: 'var(--pc-color-primary)',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: 18,
          }}
        >
          <IconBolt size={28} />
        </span>
        <h3
          style={{
            margin: '0 0 8px',
            fontSize: 18,
            fontWeight: 600,
            color: 'var(--pc-color-text)',
          }}
        >
          Reading your investments
        </h3>
        <p
          style={{
            color: 'var(--pc-color-text-muted)',
            marginBottom: 22,
            fontSize: 13,
          }}
        >
          Extracting holdings, transactions and values
        </p>
        <div
          style={{
            height: 6,
            backgroundColor: 'var(--pc-color-surface-muted)',
            borderRadius: 'var(--pc-radius-pill)',
            overflow: 'hidden',
            maxWidth: 240,
            margin: '0 auto',
          }}
        >
          <div
            style={{
              height: '100%',
              width: `${progress}%`,
              backgroundColor: 'var(--pc-color-primary)',
              transition: 'width 0.3s ease',
            }}
          />
        </div>
        <div
          style={{
            fontSize: 12,
            color: 'var(--pc-color-text-faded)',
            marginTop: 10,
          }}
        >
          {progress < 50
            ? 'Uploading your file…'
            : progress < 90
            ? 'Reading folios and holdings…'
            : 'Almost there — calculating totals'}
        </div>
      </div>
    );
  }

  if (!file) {
    return (
      <div>
        <ScreenHeader
          icon={<IconUpload size={22} />}
          title="Upload your statement"
          description="Drop your mutual fund CAS or CDSL/NSDL demat statement"
        />
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          onDragOver={(e) => {
            e.preventDefault();
            setIsDragOver(true);
          }}
          onDragLeave={() => setIsDragOver(false)}
          onDrop={handleDrop}
          aria-label="Upload a PDF"
          style={{
            width: '100%',
            border: `2px dashed ${
              isDragOver ? 'var(--pc-color-primary)' : 'var(--pc-color-border-strong)'
            }`,
            borderRadius: 'var(--pc-radius-lg)',
            padding: '36px 18px',
            textAlign: 'center',
            cursor: 'pointer',
            backgroundColor: isDragOver
              ? 'var(--pc-color-primary-soft)'
              : 'var(--pc-color-surface-subtle)',
            color: 'var(--pc-color-text)',
            fontFamily: 'inherit',
            transition: 'all 0.18s',
          }}
        >
          <input
            ref={inputRef}
            type="file"
            accept=".pdf,application/pdf"
            onChange={handleFileChange}
            style={{ display: 'none' }}
          />
          <span
            style={{
              display: 'inline-flex',
              color: 'var(--pc-color-text-muted)',
              marginBottom: 10,
            }}
          >
            <IconUpload size={34} strokeWidth={1.5} />
          </span>
          <p
            style={{
              margin: 0,
              fontSize: 15,
              color: 'var(--pc-color-text)',
              fontWeight: 500,
            }}
          >
            Drop your statement PDF here
          </p>
          <p
            style={{
              margin: '4px 0 0',
              fontSize: 13,
              color: 'var(--pc-color-text-muted)',
            }}
          >
            or click to browse
          </p>
        </button>

        {dropError && (
          <div style={{ marginTop: 10 }}>
            <StatusBanner tone="error" icon={<IconAlert size={16} />}>
              {dropError}
            </StatusBanner>
          </div>
        )}
        <div
          style={{
            marginTop: 12,
            fontSize: 11,
            color: 'var(--pc-color-text-faded)',
            textAlign: 'center',
          }}
        >
          Mutual fund CAS, or CDSL / NSDL demat statement
        </div>
      </div>
    );
  }

  return (
    <div>
      <ScreenHeader
        title="Enter your PDF password"
        description="Most statement PDFs are password-protected. If yours isn't, just leave the box blank."
      />

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          padding: 12,
          border: '1px solid var(--pc-color-border)',
          borderRadius: 'var(--pc-radius-md)',
          backgroundColor: 'var(--pc-color-surface-subtle)',
          marginBottom: 18,
        }}
      >
        <span
          style={{
            display: 'inline-flex',
            width: 36,
            height: 36,
            borderRadius: 'var(--pc-radius-sm)',
            backgroundColor: 'var(--pc-color-primary-soft)',
            color: 'var(--pc-color-primary)',
            alignItems: 'center',
            justifyContent: 'center',
            marginRight: 12,
          }}
        >
          <IconFile size={18} />
        </span>
        <div style={{ flex: 1, overflow: 'hidden' }}>
          <div
            style={{
              fontWeight: 500,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              fontSize: 13,
              color: 'var(--pc-color-text)',
            }}
          >
            {file.name}
          </div>
          <div style={{ fontSize: 11, color: 'var(--pc-color-text-faded)' }}>
            {(file.size / 1024 / 1024).toFixed(2)} MB
          </div>
        </div>
        <button
          onClick={() => {
            updateFile(null);
            onEvent?.('FILE_REMOVED');
          }}
          style={{
            background: 'none',
            border: 'none',
            color: 'var(--pc-color-primary)',
            cursor: 'pointer',
            fontSize: 12,
            fontFamily: 'inherit',
            padding: 4,
          }}
        >
          Change
        </button>
      </div>

      <label
        style={{
          display: 'block',
          fontSize: 13,
          fontWeight: 500,
          color: 'var(--pc-color-text)',
          marginBottom: 6,
        }}
      >
        PDF password
      </label>
      <PasswordInput
        data-pc-autofocus
        autoFocus
        placeholder="Leave blank if your PDF isn't password protected"
        value={password}
        onChange={(e) => updatePassword(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && file) handleSubmit();
        }}
      />
      <div
        style={{
          fontSize: 11,
          color: 'var(--pc-color-text-faded)',
          marginTop: 6,
          lineHeight: 1.5,
        }}
      >
        Common passwords: <strong>PAN in CAPS</strong> or{' '}
        <strong>DOB as DDMMYYYY</strong>. Leave blank if not protected.
      </div>

      <div style={{ marginTop: 18 }}>
        <Button variant="primary" fullWidth onClick={handleSubmit}>
          {password ? 'Import Statement' : 'Import without password'}
        </Button>
      </div>
    </div>
  );
};
